import React from 'react';
import { Target, CheckCircle2, Calculator, BarChart, BarChart4 } from 'lucide-react';
import type { CompanyAnalysis } from '../../types/geminiTypes';
import type { DcfModel } from '../../hooks/useDcfModel';
import { cn, coerceToText } from '../../lib/utils';
import { parseRadarScore } from '../../lib/formatters';
import { OracleDetailSection } from './OracleDetailSection';
import ReactMarkdown from 'react-markdown';
import { Tooltip } from '../Tooltip';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

export interface BusinessTabProps {
  analysis: CompanyAnalysis;
  dcfModel: DcfModel | null;
}

export function BusinessTab({ analysis, dcfModel }: BusinessTabProps) {
  const formatCurrencyValue = (millions: number | undefined | null) => {
    if (millions === undefined || millions === null || isNaN(millions)) return 'N/A';
    if (Math.abs(millions) >= 1000) {
      return `$${(millions / 1000).toFixed(1)}B`;
    }
    return `$${Math.round(millions)}M`;
  };

  return (
    <div className="space-y-8">
      {analysis.killerInsight && (
                <div className="mb-8 p-5 md:p-10 oi-card relative overflow-hidden">
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                        <Target className="w-4 h-4 text-oxblood" />
                      </div>
                      <h4 className="eyebrow">The Killer Insight (The Lynchpin)</h4>
                    </div>
                    <p className="text-xl text-ink leading-relaxed font-medium">
                      {coerceToText(analysis.killerInsight)}
                    </p>
                  </div>
                </div>
              )}
      {analysis.segmentAnalysis && analysis.segmentAnalysis.length > 0 && (
                  <div className="mb-12">
                    <div className="oi-card p-5 md:p-10">
                      <div className="mb-8 pb-4 border-b border-ink-200">
                        <h3 className="oi-section-title">Segment Analysis (SOTP)</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {analysis.segmentAnalysis.map((segment, idx) => (
                          <div key={idx} className={cn(
                            "p-4 md:p-8 rounded-lg border transition-all",
                            segment.isPrimaryEdge ? "bg-oxblood-50 border-oxblood" : "bg-surface border-ink-200"
                          )}>
                            <div className="flex justify-between items-start mb-6">
                              <div className="text-xl font-serif font-medium italic text-ink">{segment.name}</div>
                              {segment.isPrimaryEdge && (
                                <div className="px-3 py-1 bg-oxblood text-paper text-[8px] font-medium uppercase tracking-widest rounded-md">Primary Edge</div>
                              )}
                            </div>
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Revenue</div>
                                  <div className="text-sm font-mono font-medium text-ink">{segment.revenue}</div>
                                </div>
                                <div className="space-y-1">
                                  <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Margin</div>
                                  <div className="text-sm font-mono font-medium text-ink">{segment.margin}</div>
                                </div>
                              </div>
                              <div className="pt-4 border-t border-ink-200 grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Multiple</div>
                                  <div className="text-sm font-mono font-medium text-ink">{segment.compMultiple}</div>
                                </div>
                                <div className="space-y-1">
                                  <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Implied</div>
                                  <div className="text-sm font-mono font-medium text-[var(--color-success-700)]">{segment.impliedValue}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
      {analysis.oracleFramework && (
                  <div className="space-y-8">
                    <div className="oi-card p-5 md:p-10 overflow-hidden relative">
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-8">
                        <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-oxblood" />
                        </div>
                        <div className="eyebrow">Oracle Decision Framework</div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-16 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                          {[
                            { key: "understandable", label: "Circle of Competence", value: analysis.oracleFramework?.understandable?.value, rationale: analysis.oracleFramework?.understandable?.rationale },
                            { key: "moat", label: "Durable Moat", value: analysis.oracleFramework?.moat?.value, rationale: analysis.oracleFramework?.moat?.rationale },
                            { key: "management", label: "Management", value: analysis.oracleFramework?.management?.value, rationale: analysis.oracleFramework?.management?.rationale },
                            { key: "marginOfSafety", label: "Margin of Safety", value: analysis.oracleFramework?.marginOfSafety?.value, rationale: analysis.oracleFramework?.marginOfSafety?.rationale },
                            { key: "longTerm", label: "Long-term Predictability", value: analysis.oracleFramework?.longTerm?.value, rationale: analysis.oracleFramework?.longTerm?.rationale },
                            { key: "stability", label: "Business Stability", value: analysis.oracleFramework?.stability?.value, rationale: analysis.oracleFramework?.stability?.rationale }
                          ].map((item) => (
                            <div key={item.key} className="flex flex-col gap-3 group/item border-l border-ink-200 pl-6 hover:border-oxblood/30 transition-colors">
                              <div className="flex justify-between items-baseline">
                                <div className="text-[8px] font-medium uppercase tracking-[0.2em] text-ink-500 group-hover/item:text-ink-600 transition-colors">{item.label}</div>
                                <div className="text-[10px] font-mono font-medium text-oxblood bg-oxblood-50 px-2 py-0.5 rounded-sm uppercase tracking-widest">{item.value}</div>
                              </div>
                              <p className="text-sm text-ink-600 leading-relaxed group-hover/item:text-ink transition-colors">
                                {item.rationale}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="flex flex-col gap-6 w-full">
                          <div className="flex flex-col items-center justify-center gap-3 p-6 bg-surface-2 rounded-lg border border-ink-200 text-center">
                            <div className="text-[8px] font-medium uppercase tracking-[0.3em] text-ink-500">Final Verdict</div>
                            <div className={cn(
                              "px-10 py-3 rounded-md text-xs font-medium uppercase tracking-[0.2em] inline-block",
                              dcfModel.decision.color === "green" ? "bg-success-600 text-paper" :
                              dcfModel.decision.color === "amber" ? "bg-warning-600 text-paper" :
                              "bg-danger-600 text-paper"
                            )}>
                              {dcfModel.decision.action}{dcfModel.decision.qualifier}
                            </div>
                            {analysis.capitalAllocationDecision && (
                              <>
                                <div className="text-[8px] font-medium uppercase tracking-[0.3em] text-ink-500 mt-3 hidden xl:block">Position Sizing</div>
                                <div className="text-sm font-serif font-medium italic text-ink-600 leading-relaxed hidden xl:block">
                                  {(dcfModel.decision.action === "PASS" || dcfModel.decision.action === "AVOID" || dcfModel.decision.action === "WATCH") 
                                    ? (dcfModel.decision.action === "WATCH" ? "WAIT FOR PULLBACK / ACCUMULATE ON WEAKNESS" : dcfModel.decision.action) 
                                    : analysis.capitalAllocationDecision}
                                </div>
                              </>
                            )}
                          </div>

                          <div className="w-full h-[350px] bg-surface-2 rounded-lg p-4 md:p-8 border border-ink-200 flex items-center justify-center relative">
                            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                              <RadarChart cx="50%" cy="50%" outerRadius="55%" data={[
                                { subject: 'Moat', A: parseRadarScore(analysis.oracleFramework?.moat?.value), fullMark: 5 },
                                { subject: 'Management', A: parseRadarScore(analysis.oracleFramework?.management?.value), fullMark: 5 },
                                { subject: 'Competence', A: parseRadarScore(analysis.oracleFramework?.understandable?.value), fullMark: 5 },
                                { subject: 'Safety', A: parseRadarScore(analysis.oracleFramework?.marginOfSafety?.value), fullMark: 5 },
                                { subject: 'Predictability', A: parseRadarScore(analysis.oracleFramework?.longTerm?.value), fullMark: 5 },
                                { subject: 'Stability', A: parseRadarScore(analysis.oracleFramework?.stability?.value), fullMark: 5 },
                              ]}>
                                <PolarGrid stroke="rgba(33,28,22,0.10)" />
                                <PolarAngleAxis 
                                  dataKey="subject" 
                                  tick={{ fill: 'rgba(107,96,86,1)', fontSize: 9, fontWeight: 500, letterSpacing: '0.1em' }} 
                                />
                                <PolarRadiusAxis angle={30} domain={[0, 5]} tick={false} axisLine={false} />
                                <Radar
                                  name="Oracle Score"
                                  dataKey="A"
                                  stroke="#6E1D24"
                                  fill="#6E1D24"
                                  fillOpacity={0.15}
                                  strokeWidth={2}
                                />
                              </RadarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 mt-4 border-t border-ink-200 flex justify-end">
                        <div className="text-[7px] text-ink-300 font-mono uppercase tracking-[0.4em] pb-1">
                          Oracle Decision Engine v5.0 // Secure Analysis
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
      {(analysis.unitEconomics?.length > 0 || analysis.peerBenchmarking?.length > 0) && (
                <div className="mb-8 flex flex-col gap-8">
                  {analysis.unitEconomics && analysis.unitEconomics.length > 0 && (
                    <div className="oi-card p-4 md:p-8">
                      <div className="flex items-center gap-3 mb-8">
                        <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                          <Calculator className="w-4 h-4 text-oxblood" />
                        </div>
                        <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">Unit Economics</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {analysis.unitEconomics.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_130px] gap-4 items-start border-b border-ink-200 pb-4 last:border-0 last:pb-0 md:border-b-0 md:border-r md:pr-6 md:last:border-r-0 group">
                            <div className="space-y-1">
                              <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 group-hover:text-ink-600 transition-colors">{item.label}</div>
                              <div className="text-base font-mono font-medium text-ink leading-tight">{item.value}</div>
                            </div>
                            <div className="text-right pt-1">
                              <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500 mb-1">Benchmark</div>
                              <div className="text-[10px] font-mono font-medium text-ink-600 bg-surface-2 px-2 py-1 rounded-lg border border-ink-200 leading-snug">
                                {item.benchmark}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysis.peerBenchmarking && analysis.peerBenchmarking.length > 0 && (
                    <div className="oi-card p-6">
                      <h5 className="font-medium text-ink uppercase text-[11px] tracking-[0.4em] mb-8 flex items-center gap-4">
                        <BarChart4 className="w-5 h-5" />
                        Peer Benchmarking
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-0">
                        {Object.entries(
                          analysis.peerBenchmarking.reduce((acc, item) => {
                            if (!acc[item.peer]) acc[item.peer] = [];
                            acc[item.peer].push(item);
                            return acc;
                          }, {} as Record<string, typeof analysis.peerBenchmarking>)
                        ).map(([peer, items], peerIdx, arr) => (
                          <div key={peerIdx} className={cn(
                            "space-y-3 pb-6",
                            peerIdx < arr.length - 1 ? "border-b border-ink-200" : "",
                            Math.floor(peerIdx / 3) < Math.floor((arr.length - 1) / 3) ? "md:border-b md:border-ink-200" : "md:border-b-0",
                            peerIdx % 3 === 0 ? (peerIdx !== arr.length - 1 ? "md:border-r md:border-ink-200 md:pr-6" : "md:pr-6") :
                            peerIdx % 3 === 1 ? (peerIdx !== arr.length - 1 ? "md:border-r md:border-ink-200 md:px-6" : "md:px-6") :
                            "md:pl-6"
                          )}>
                            <h5 className="text-xs font-medium uppercase tracking-[0.2em] text-ink-500 border-b border-ink-200 pb-2">
                              {peer}
                            </h5>
                            <div className="space-y-4">
                              {items.map((item, idx) => {
                                const isLongComparison = item.comparison.length > 15;
                                const comparisonText = isLongComparison ? "NOTE" : item.comparison;
                                const contextText = item.context || (isLongComparison ? item.comparison : null);

                                const isPositive = item.comparison.toLowerCase().includes("better") || item.comparison.toLowerCase().includes("favorable") || item.comparison.toLowerCase().includes("higher") || (item.comparison.toLowerCase().includes("lower") && !item.comparison.toLowerCase().includes("risk"));

                                return (
                                  <div key={idx} className="flex flex-col gap-1 pb-3 border-b border-ink-200 last:border-0 last:pb-0">
                                    <div className="flex justify-between items-start gap-2 mb-1">
                                      <div className="text-[10px] font-medium text-ink leading-tight pr-2 uppercase tracking-wide">{item.metric}</div>
                                      <div className="text-sm font-mono font-medium text-ink leading-tight shrink-0">{item.value}</div>
                                    </div>

                                    <div className="flex flex-col items-end gap-1.5 w-full">
                                      {!isLongComparison && (
                                        <div className={cn(
                                          "text-[9px] font-medium uppercase tracking-widest px-2 py-0.5 rounded inline-block text-center",
                                          isPositive ? "bg-[var(--color-success-50)] text-[var(--color-success-700)]" : "bg-[var(--color-warning-50)] text-[var(--color-warning-700)]"
                                        )}>
                                          {comparisonText}
                                        </div>
                                      )}

                                      {contextText && (
                                        <div className={cn(
                                          "text-[8px] font-medium uppercase tracking-widest px-2 py-1.5 rounded text-right whitespace-normal break-words w-full leading-relaxed",
                                          isPositive ? "bg-[var(--color-success-50)] text-[var(--color-success-700)]" : "bg-[var(--color-warning-50)] text-[var(--color-warning-700)]"
                                        )}>
                                          {contextText}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
      {analysis.lollapaloozaEffects && (
                <div className="mb-8 p-4 md:p-8 oi-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1.5 h-1.5 bg-oxblood rounded-full" />
                    <h4 className="eyebrow">Lollapalooza Effects</h4>
                  </div>
                  <p className="text-base text-ink-600 leading-relaxed relative z-10">
                    {coerceToText(analysis.lollapaloozaEffects)}
                  </p>
                </div>
              )}
      {analysis.killerInsight && (
                <div className="mb-8 p-4 md:p-8 oi-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1.5 h-1.5 bg-oxblood rounded-full" />
                    <h4 className="eyebrow">Killer Insight</h4>
                  </div>
                  <p className="text-xl text-ink leading-relaxed relative z-10 font-medium tracking-tight">
                    "{coerceToText(analysis.killerInsight)}"
                  </p>
                </div>
              )}
    </div>
  );
}
